import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type { Session } from '@supabase/supabase-js';
import { CreateEventPanel } from './components/CreateEventPanel';
import { EventWorkspace } from './components/EventWorkspace';
import { JoinEventPanel } from './components/JoinEventPanel';
import { NotificationsPanel } from './components/NotificationsPanel';
import { ProfilePanel } from './components/ProfilePanel';
import { eventAllowsUploads, eventIsViewOnly, getEventLifecycleLabel } from './domain/eventLifecycle';
import { isSupabaseConfigured } from './lib/supabase';
import {
  getCurrentSession,
  signInWithPassword,
  signOut,
  subscribeToAuthChanges,
} from './services/auth';
import {
  loadEventForUser,
  loadVisibleEventsForUser,
  subscribeToVisibleEventChanges,
} from './services/events';
import type { WebJoinResult } from './services/portal';
import type { WebEvent } from './types/events';
import './components/portal.css';
import './components/legacy-dashboard.css';

type PortalSection = 'home' | 'events' | 'host' | 'join' | 'notifications' | 'profile';
type WorkspaceStart = 'gallery' | 'people' | 'host';

function formatEventDate(event: WebEvent): string {
  const value = event.start_time || event.event_date || event.created_at;
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return 'Date TBD';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function eventCover(event?: WebEvent | null): string | null {
  return event?.cover_photo_url || event?.preview_thumbnail_urls?.[0] || null;
}

function initials(value: string): string {
  const text = value.trim();
  if (!text) return 'G';
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

type SidebarProps = {
  active: PortalSection | 'event';
  activeCount: number;
  onNavigate: (section: PortalSection) => void;
  onCreate: () => void;
};

function DashboardSidebar({ active, activeCount, onNavigate, onCreate }: SidebarProps) {
  const navItems: Array<[PortalSection, string, string]> = [
    ['home', '⌂', 'Home'],
    ['events', '◫', 'My Events'],
    ['host', '⚙', 'Host Tools'],
    ['join', '#', 'Join Event'],
    ['notifications', '✦', 'Notifications'],
    ['profile', '◉', 'Profile'],
  ];

  return (
    <aside className="legacy-sidebar">
      <button className="legacy-side-logo" type="button" onClick={() => onNavigate('home')}>
        <span className="legacy-brand-mark">G</span>
        <span>Gathrd</span>
      </button>

      <nav className="legacy-nav" aria-label="Gathrd dashboard">
        {navItems.slice(0, 2).map(([key, icon, label]) => (
          <button key={key} className={active === key ? 'active' : ''} type="button" onClick={() => onNavigate(key)}>
            <span>{icon}</span>{label}
          </button>
        ))}
        <button type="button" onClick={onCreate}>
          <span>＋</span>Create Event
        </button>
        {navItems.slice(2).map(([key, icon, label]) => (
          <button key={key} className={active === key ? 'active' : ''} type="button" onClick={() => onNavigate(key)}>
            <span>{icon}</span>{label}
          </button>
        ))}
      </nav>

      <div className="legacy-sidebar-card">
        <p className="eyebrow">Active memories</p>
        <strong>{activeCount}</strong>
        <span>events ready for uploads</span>
        <a href="https://wegathrd.com/download.html" target="_blank" rel="noreferrer">Get the mobile app →</a>
      </div>
    </aside>
  );
}

type EventCardProps = {
  event: WebEvent;
  index: number;
  onOpen: (event: WebEvent, start?: WorkspaceStart) => void;
};

function DashboardEventCard({ event, index, onOpen }: EventCardProps) {
  const cover = eventCover(event);
  const viewOnly = eventIsViewOnly(event);
  const canManage = event.access_role === 'organizer' || event.access_role === 'co_admin';
  const coverStyle = cover
    ? { backgroundImage: `linear-gradient(135deg, rgba(15,23,42,.12), rgba(15,23,42,.52)), url("${cover}")` }
    : undefined;

  return (
    <article className="legacy-event-card">
      <div className={`legacy-album-cover legacy-cover-${(index % 5) + 1}`} style={coverStyle}>
        <div className="legacy-cover-badges">
          <span>{event.access_role.replace('_', ' ')}</span>
          <span>{getEventLifecycleLabel(event)}</span>
        </div>
        <strong>{event.code || 'GATHRD'}</strong>
      </div>
      <div className="legacy-event-body">
        <div className="legacy-badges">
          <span className={`legacy-badge ${event.access_role === 'organizer' ? 'green' : ''}`}>{event.access_role.replace('_', ' ')}</span>
          {event.uploads_locked && <span className="legacy-badge amber">Uploads locked</span>}
          {viewOnly && <span className="legacy-badge amber">View only</span>}
        </div>
        <h3>{event.name}</h3>
        <p className="legacy-help">{event.description || event.location_name || 'A private shared event album.'}</p>
        <p className="legacy-event-meta">
          <span>{formatEventDate(event)}</span>
          <span>{event.location_name || event.location_address || 'Location not set'}</span>
        </p>
        <div className="legacy-actions">
          <button className="legacy-btn primary" type="button" onClick={() => onOpen(event, 'gallery')}>Open Gallery</button>
          {canManage && <button className="legacy-btn secondary" type="button" onClick={() => onOpen(event, 'host')}>Manage</button>}
        </div>
      </div>
    </article>
  );
}

export function App() {
  const configured = isSupabaseConfigured();
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [events, setEvents] = useState<WebEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<WebEvent | null>(null);
  const [workspaceStart, setWorkspaceStart] = useState<WorkspaceStart>('gallery');
  const [section, setSection] = useState<PortalSection>('home');
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eventRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!configured) {
      setAuthReady(true);
      return;
    }

    let active = true;
    getCurrentSession()
      .then((current) => {
        if (active) setSession(current);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : 'Unable to restore session.');
      })
      .finally(() => {
        if (active) setAuthReady(true);
      });

    const unsubscribe = subscribeToAuthChanges((_event, nextSession) => {
      if (active) setSession(nextSession);
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [configured]);

  const refreshEvents = useCallback(async () => {
    const userId = session?.user.id;
    if (!userId) {
      setEvents([]);
      return;
    }

    setLoadingEvents(true);
    setError(null);
    try {
      const nextEvents = await loadVisibleEventsForUser(userId);
      setEvents(nextEvents);
      setSelectedEvent((current) =>
        current ? nextEvents.find((event) => event.id === current.id) ?? null : null,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load events.');
    } finally {
      setLoadingEvents(false);
    }
  }, [session?.user.id]);

  const memberEventIdsKey = useMemo(
    () =>
      events
        .filter((event) => event.organizer_id !== session?.user.id)
        .map((event) => event.id)
        .filter(Boolean)
        .sort()
        .join(','),
    [events, session?.user.id],
  );

  const scheduleEventRefresh = useCallback(() => {
    if (eventRefreshTimerRef.current) clearTimeout(eventRefreshTimerRef.current);
    eventRefreshTimerRef.current = setTimeout(() => {
      eventRefreshTimerRef.current = null;
      void refreshEvents();
    }, 250);
  }, [refreshEvents]);

  useEffect(() => {
    if (!session?.user.id) {
      setEvents([]);
      setSelectedEvent(null);
      return;
    }
    void refreshEvents();
  }, [refreshEvents, session?.user.id]);

  useEffect(() => {
    const userId = session?.user.id;
    if (!userId) return;

    const memberEventIds = memberEventIdsKey ? memberEventIdsKey.split(',') : [];
    const unsubscribe = subscribeToVisibleEventChanges(userId, memberEventIds, scheduleEventRefresh);
    return () => {
      unsubscribe();
      if (eventRefreshTimerRef.current) {
        clearTimeout(eventRefreshTimerRef.current);
        eventRefreshTimerRef.current = null;
      }
    };
  }, [memberEventIdsKey, scheduleEventRefresh, session?.user.id]);

  async function handleSignIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setAuthBusy(true);
    setError(null);
    try {
      const nextSession = await signInWithPassword(email.trim(), password);
      setSession(nextSession);
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to sign in.');
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleSignOut() {
    setAuthBusy(true);
    setError(null);
    try {
      await signOut();
      setSession(null);
      setEvents([]);
      setSelectedEvent(null);
      setSection('home');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to sign out.');
    } finally {
      setAuthBusy(false);
    }
  }

  function handleEventUpdated(updated: WebEvent) {
    setEvents((current) => current.map((event) => (event.id === updated.id ? updated : event)));
    setSelectedEvent(updated);
  }

  function handleEventDeleted(eventId: string) {
    setEvents((current) => current.filter((event) => event.id !== eventId));
    setSelectedEvent(null);
    setSection('events');
  }

  function navigate(next: PortalSection) {
    setSelectedEvent(null);
    setSection(next);
  }

  function openEvent(event: WebEvent, start: WorkspaceStart = 'gallery') {
    setWorkspaceStart(start);
    setSelectedEvent(event);
  }

  async function openEventById(eventId: string) {
    const userId = session?.user.id;
    if (!userId || !eventId) return;
    setError(null);
    try {
      const event = await loadEventForUser(eventId, userId);
      if (!event) throw new Error('This event is not currently available to your account.');
      openEvent(event);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to open this event.');
    }
  }

  async function handleJoined(result: WebJoinResult) {
    await refreshEvents();
    if (result.status === 'requested') return;
    if (result.event) {
      openEvent(result.event);
      return;
    }
    await openEventById(result.eventId);
  }

  function handleCreated(event: WebEvent) {
    setEvents((current) => [event, ...current.filter((item) => item.id !== event.id)]);
    setShowCreate(false);
    openEvent(event, 'host');
  }

  const filteredEvents = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return events;
    return events.filter((event) =>
      `${event.name || ''} ${event.description || ''} ${event.code || ''} ${event.location_name || ''} ${event.location_address || ''}`
        .toLowerCase()
        .includes(query),
    );
  }, [events, search]);

  const counts = useMemo(() => {
    const owned = events.filter((event) => event.access_role === 'organizer').length;
    const joined = events.filter((event) => event.access_role !== 'organizer').length;
    const active = events.filter((event) => eventAllowsUploads(event)).length;
    return { total: events.length, owned, joined, active };
  }, [events]);

  const hostEvents = useMemo(
    () => filteredEvents.filter((event) => event.access_role === 'organizer' || event.access_role === 'co_admin'),
    [filteredEvents],
  );
  const featured = filteredEvents.find((event) => eventAllowsUploads(event)) || filteredEvents[0] || events[0] || null;
  const featuredCover = eventCover(featured);
  const displayName = String(session?.user.user_metadata?.full_name || session?.user.email || 'Gathrd member');
  const firstName = displayName.split(/\s+/)[0] || 'there';

  if (!configured) {
    return (
      <main className="foundation-shell">
        <section className="foundation-card compact-card">
          <p className="eyebrow">Gathrd Web</p>
          <h1>Connect the web client</h1>
          <p>Add `web/.env.local` with the existing public Supabase URL and anon key.</p>
          <p className="muted">No service-role or server secrets belong in the browser.</p>
        </section>
      </main>
    );
  }

  if (!authReady) {
    return (
      <main className="foundation-shell">
        <section className="foundation-card compact-card">
          <p className="eyebrow">Gathrd Web</p>
          <h1>Restoring session…</h1>
        </section>
      </main>
    );
  }

  if (!session) {
    return (
      <main className="foundation-shell legacy-auth-shell">
        <section className="foundation-card auth-card legacy-auth-card">
          <div className="legacy-auth-brand"><span className="legacy-brand-mark">G</span><strong>Gathrd</strong></div>
          <p className="eyebrow">Shared moments, together.</p>
          <h1>Sign in to Gathrd</h1>
          <p className="muted">Use the same account as the mobile app.</p>
          <form className="auth-form" onSubmit={handleSignIn}>
            <label>
              Email
              <input type="email" autoComplete="email" value={email} onChange={(inputEvent) => setEmail(inputEvent.target.value)} required />
            </label>
            <label>
              Password
              <input type="password" autoComplete="current-password" value={password} onChange={(inputEvent) => setPassword(inputEvent.target.value)} required />
            </label>
            <button type="submit" disabled={authBusy}>{authBusy ? 'Signing in…' : 'Sign in'}</button>
          </form>
          {error && <p className="error-banner">{error}</p>}
        </section>
      </main>
    );
  }

  if (selectedEvent) {
    return (
      <div className="legacy-dashboard">
        <div className="legacy-layout">
          <DashboardSidebar active="event" activeCount={counts.active} onNavigate={navigate} onCreate={() => setShowCreate(true)} />
          <main className="legacy-main legacy-main-event">
            <EventWorkspace
              event={selectedEvent}
              session={session}
              authBusy={authBusy}
              initialSection={workspaceStart}
              onBack={() => { setSelectedEvent(null); setSection('events'); }}
              onSignOut={handleSignOut}
              onEventUpdated={handleEventUpdated}
              onEventDeleted={handleEventDeleted}
            />
          </main>
        </div>
        {showCreate && <CreateEventPanel userId={session.user.id} onClose={() => setShowCreate(false)} onCreated={handleCreated} />}
      </div>
    );
  }

  const renderEventCards = (items: WebEvent[]) => (
    <div className="legacy-event-list">
      {items.map((event, index) => <DashboardEventCard key={event.id} event={event} index={index} onOpen={openEvent} />)}
    </div>
  );

  return (
    <div className="legacy-dashboard">
      <div className="legacy-layout">
        <DashboardSidebar active={section} activeCount={counts.active} onNavigate={navigate} onCreate={() => setShowCreate(true)} />
        <main className="legacy-main">
          <header className="legacy-topbar">
            <div className="legacy-search-wrap">
              <span>⌕</span>
              <input className="legacy-search" value={search} onChange={(input) => setSearch(input.target.value)} placeholder="Search events, codes, places" />
            </div>
            <button className="legacy-btn primary" type="button" onClick={() => setShowCreate(true)}>Create Event</button>
            <button className="legacy-btn secondary" type="button" onClick={() => setSection('join')}>Join Code</button>
            <button className="legacy-avatar" type="button" title={session.user.email || ''} onClick={() => setSection('profile')}>{initials(displayName)}</button>
          </header>

          {error && <p className="error-banner legacy-global-error">{error}</p>}

          {section === 'home' && (
            <div className="legacy-home-view">
              <section
                className="legacy-hero-dashboard"
                style={featuredCover ? { backgroundImage: `linear-gradient(135deg, rgba(15,23,42,.20), rgba(15,23,42,.58)), url("${featuredCover}")` } : undefined}
              >
                <div className="legacy-hero-content">
                  <p className="eyebrow">Welcome back, {firstName}</p>
                  <h1>Your memories are gathering.</h1>
                  <p>{featured ? <>Continue <strong>{featured.name}</strong>, invite guests, or upload photos from your computer.</> : 'Create your first private event album or join an event with a code.'}</p>
                  <div className="legacy-hero-actions">
                    {featured && <button className="legacy-btn light" type="button" onClick={() => openEvent(featured)}>Open Featured Event</button>}
                    <button className="legacy-btn ghost-light" type="button" onClick={() => setShowCreate(true)}>Create Event</button>
                    <button className="legacy-btn ghost-light" type="button" onClick={() => setSection('join')}>Join with Code</button>
                  </div>
                </div>
                <div className="legacy-floating-album-card">
                  <span>{featured ? getEventLifecycleLabel(featured) : 'Ready to begin'}</span>
                  <strong>{featured?.name || 'New Event'}</strong>
                  <small>{featured ? `${formatEventDate(featured)} · ${featured.code || 'No code'}` : 'Invite guests in seconds'}</small>
                </div>
              </section>

              <section className="legacy-stats-row">
                <article><strong>{counts.total}</strong><span>Total events</span><small>All albums connected to your account</small></article>
                <article><strong>{counts.owned}</strong><span>Owned</span><small>Events you host and control</small></article>
                <article><strong>{counts.joined}</strong><span>Joined</span><small>Events shared with you</small></article>
                <article><strong>{counts.active}</strong><span>Active</span><small>Open or ready for uploads</small></article>
              </section>

              <div className="legacy-dashboard-grid">
                <section className="legacy-card">
                  <div className="legacy-section-head">
                    <div><h2>My events</h2><p className="legacy-help">Open an album, invite guests, or manage host settings.</p></div>
                    <button className="legacy-btn secondary" type="button" onClick={() => setSection('events')}>View all</button>
                  </div>
                  {filteredEvents.length ? renderEventCards(filteredEvents.slice(0, 6)) : <div className="legacy-empty"><h3>No events yet</h3><p>Create your first event or join one with a code.</p></div>}
                </section>
                <aside className="legacy-card legacy-quick-panel">
                  <h3>Quick actions</h3>
                  <button className="legacy-quick-action" type="button" onClick={() => setShowCreate(true)}><span>＋</span><strong>Create new event</strong><small>Start a private event album</small></button>
                  <button className="legacy-quick-action" type="button" onClick={() => setSection('join')}><span>#</span><strong>Join with code</strong><small>Enter a host or invite code</small></button>
                  <button className="legacy-quick-action" type="button" onClick={() => setSection('host')}><span>⚙</span><strong>Host tools</strong><small>Manage roles, invites, uploads</small></button>
                  <a className="legacy-quick-action" href="https://wegathrd.com/download.html" target="_blank" rel="noreferrer"><span>↗</span><strong>Mobile app</strong><small>Open Gathrd on iOS or Android</small></a>
                </aside>
              </div>
            </div>
          )}

          {section === 'events' && (
            <>
              <section className="legacy-page-head">
                <div><p className="eyebrow">Private albums</p><h1>My Events</h1><p>All events you created or joined, synchronized with the Gathrd mobile app.</p></div>
                <div className="legacy-actions"><button className="legacy-btn primary" type="button" onClick={() => setShowCreate(true)}>Create Event</button><button className="legacy-btn secondary" type="button" onClick={() => setSection('join')}>Join Event</button></div>
              </section>
              {filteredEvents.length ? renderEventCards(filteredEvents) : <div className="legacy-card legacy-empty"><h2>No matching events</h2><p>Try another search, create an event, or join one with a code.</p></div>}
            </>
          )}

          {section === 'host' && (
            <>
              <section className="legacy-page-head">
                <div><p className="eyebrow">Host controls</p><h1>Manage Events</h1><p>People, roles, invites, uploads, access, archive, and lifecycle controls.</p></div>
              </section>
              {hostEvents.length ? renderEventCards(hostEvents) : <div className="legacy-card legacy-empty"><h2>No host tools yet</h2><p>Events where you are owner or co-admin will appear here.</p></div>}
            </>
          )}

          {section === 'join' && <div className="legacy-panel-page"><JoinEventPanel userId={session.user.id} requesterName={displayName} onJoined={(result) => void handleJoined(result)} /></div>}
          {section === 'notifications' && <div className="legacy-panel-page"><NotificationsPanel userId={session.user.id} onOpenEvent={(eventId) => void openEventById(eventId)} /></div>}
          {section === 'profile' && <div className="legacy-panel-page"><ProfilePanel userId={session.user.id} accountEmail={session.user.email} onSignOut={handleSignOut} /></div>}
        </main>
      </div>

      {showCreate && <CreateEventPanel userId={session.user.id} onClose={() => setShowCreate(false)} onCreated={handleCreated} />}
    </div>
  );
}
